import { format } from 'date-fns'
import partition from 'lodash-es/partition'

import { DATE_HOURS_FORMAT, DAY_FIRST_HOUR } from '../../../constants/date'
import { createTime } from '../../../utils/date'
import { CALENDAR_GRID_MINUTES_PER_ROW } from '../CalendarGrid.constants'
import {
  getCalendarGridItemsByDay,
  getCalendarGridTimedItems,
  getIsCalendarGridToday,
} from '../CalendarGrid.utils'
import {
  getCalendarGridDayTime,
  getCalendarGridItemMinuteOffsets,
  getCalendarGridLocalHour,
  getCalendarGridMinuteOffset,
} from '../CalendarGrid.utils.time'

import {
  CALENDAR_GRID_WEEK_FIRST_HOUR,
  CALENDAR_GRID_WEEK_FIRST_LANE,
  CALENDAR_GRID_WEEK_HOUR_COUNT,
  CALENDAR_GRID_WEEK_LAST_POSSIBLE_FIRST_HOUR,
  CALENDAR_GRID_WEEK_SINGLE_DAY_COUNT,
  CALENDAR_GRID_WEEK_SINGLE_LANE_COUNT,
  CALENDAR_GRID_WEEK_WINDOW_MINUTES,
} from './CalendarGridWeek.constants'

function getIsCalendarGridItemInWindow({ firstHour, item, locale, timezone }) {
  const { endOffset, startOffset } = getCalendarGridItemMinuteOffsets({
    firstHour,
    item,
    locale,
    timezone,
  })

  const isStartedInWindow = getIsCalendarGridTimeInWindow({
    firstHour,
    locale,
    timezone,
    value: item.start,
  })
  const isRunningAtWindowStart = startOffset < 0 && endOffset > 0

  return isStartedInWindow || isRunningAtWindowStart
}

export function getIsCalendarGridTimeInWindow({
  firstHour,
  locale,
  timezone,
  value,
}) {
  const offset = getCalendarGridMinuteOffset({
    firstHour,
    locale,
    timezone,
    value,
  })

  const isAfterWindowStart = offset >= 0
  const isBeforeWindowEnd = offset < CALENDAR_GRID_WEEK_WINDOW_MINUTES

  return isAfterWindowStart && isBeforeWindowEnd
}

// A day that continues an item from the day before starts at 00:00 and
// must not pull the window there
export function getCalendarGridFirstHour({ items, locale, timezone }) {
  const hours = getCalendarGridTimedItems(items)
    .filter(item => !item.isContinued)
    .map(item =>
      getCalendarGridLocalHour({ locale, timezone, value: item.start })
    )

  return hours.length > 0
    ? Math.max(
        DAY_FIRST_HOUR,
        Math.min(...hours, CALENDAR_GRID_WEEK_LAST_POSSIBLE_FIRST_HOUR)
      )
    : CALENDAR_GRID_WEEK_FIRST_HOUR
}

export function getCalendarGridHourLabels(firstHour) {
  return Array.from({ length: CALENDAR_GRID_WEEK_HOUR_COUNT }, (_, index) => {
    const hour = firstHour + index

    return {
      label: format(createTime({ hours: hour }), DATE_HOURS_FORMAT),
      value: hour,
    }
  })
}

export function getCalendarGridWeekItemsByDay({
  firstHour,
  items,
  locale,
  timezone,
}) {
  const [visibleItems, outsideHoursItems] = partition(
    getCalendarGridTimedItems(items),
    item => getIsCalendarGridItemInWindow({ firstHour, item, locale, timezone })
  )

  return {
    itemsByDay: getCalendarGridItemsByDay({
      items: visibleItems,
      locale,
      timezone,
    }),
    outsideHoursItemsByDay: getCalendarGridItemsByDay({
      items: outsideHoursItems,
      locale,
      timezone,
    }),
  }
}

export function getIsCalendarGridTodayTinted({ day, days, today }) {
  const hasSeveralDays = days.length > CALENDAR_GRID_WEEK_SINGLE_DAY_COUNT

  return hasSeveralDays && getIsCalendarGridToday({ day, today })
}

export function getCalendarGridCellStart({ day, hour, timezone }) {
  return getCalendarGridDayTime({
    day,
    minutes: hour * CALENDAR_GRID_MINUTES_PER_ROW,
    timezone,
  })
}

export function getCalendarGridGroupStyle({
  endOffset,
  lane = CALENDAR_GRID_WEEK_FIRST_LANE,
  laneCount = CALENDAR_GRID_WEEK_SINGLE_LANE_COUNT,
  startOffset,
}) {
  return {
    '--calendar-grid-block-lane': String(lane),
    '--calendar-grid-block-lanes': String(laneCount),
    '--calendar-grid-block-rows': String(
      (endOffset - startOffset) / CALENDAR_GRID_MINUTES_PER_ROW
    ),
    '--calendar-grid-block-start': String(
      startOffset / CALENDAR_GRID_MINUTES_PER_ROW
    ),
  }
}

export function getCalendarGridWeekStyle(days) {
  return { '--calendar-grid-day-count': String(days.length) }
}
